import { boundarySource } from '../boundary-journal.js';
import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, int, itemField, record, requiredInt, upper } from '../context.js';
import { nextSpellPlan } from '../../sanguine/next-spell.js';
import { preflightNextSpell, settleNextSpell } from '../next-spell.js';
import { type JanusInput } from '../janus.js';
import { dispatchNextSpell } from '../next-spell.js';
import { counterspellCheck } from '../counterspell.js';
import { armorBendingRoll } from '../armor-bending.js';
import { captureAreaVfx } from '../area-vfx.js';
import { hostileSpellMiscastHolder, hostileSpellMiscastRoll } from '../hostile-spell-miscast.js';
import { adjustCharges, janusPostRollChangeAbilityResources, preflightPostRollChargeResources } from '../charges/adjust-charges.js';
import { abilityChargeEntry } from '../charges/charge-entry.js';
import { manaSpendPreDraw, fireManaSpend } from '../mana-spend-trigger.js';
import { planCasterUsesItemType, fireCasterUsesItemType } from '../caster-uses-item-type.js';
import { ctxSpellManaDiscount, discountedSpellMana, ctxProviderReaders, nextSpellManaDiscountCarriers } from '../caster-spell-layers.js';
import { summonApCost } from '../summon.js';
import { settleSourceCharges, sourceItemType } from '../charged-cast.js';
import { spellCastIsFree } from '../spell-grant-mode.js';
import { preflightInnateSpellUse, settleInnateSpellUse } from '../innate-spell-use.js';
import { arcaneArrowDeliveryMatches, mysteriousCarriers, refundMysteriousCarrier } from '../cast-modifier-ports.js';
import { planStoredStatusRemoval, removeStoredStatus } from '../statuses/status-effect-removal-helpers.js';
import { consumeSpellMaterials } from '../spell-materials.js';
import { setMana } from '../resource-reduction.js';
import { manaReductionDelta } from '../../resources/reduction-guard.js';
import { resourceReductionReaders } from '../resource-reduction.js';
import { applyIceWallDisplacements } from '../aoe/painted-wall-ice-displacement-state';

/** ClassMechanic 'aether' (Spellblade). */
const AETHER_MECHANIC = '5FF2F49A-7667-D686-2BC6-32623375A091';

/** R-128: the engine-fired cast in progress is THIS spell on THIS caster (ctx.engineFiredCast — never a cast nested inside it). */
const engineFired = (ctx: JanusCtx, ability: Row, caster: Row): boolean => !!ctx.engineFiredCast
  && upper(itemField(ability, 'item_id')) === ctx.engineFiredCast.abilityId && upper(record(caster.encounter_creatures)?.encounter_creature_id) === ctx.engineFiredCast.casterId;

/** includes/functions/janus/core/template_spell_roll.php:template_spell_roll */
export function spendSpellAction(ctx: JanusCtx, ability: Row, caster: Row, countered = false): void {
  if (ctx.arcaneArrowDelivery?.caster_id === ecId(caster) && arcaneArrowDeliveryMatches(ctx, ability, 'arcane_arrow_cast')) return;
  if (!ctx.read.inCombat(ctx.encounter, caster)) return;
  const castingTime = String(ctx.read.value(ability, 'spell_casting_time') ?? '3ap');
  const casterEc = ec(caster);
  // A timed finish bypasses the fresh-action AP gate. PHP still debits its casting cost, then
  // update_encounter_creature_stats clamps AP to [0, 127], including an already exhausted caster (TF-9 Augury).
  const finish = ctx.timedFinish?.casterId === ecId(caster)
    && ctx.timedFinish?.abilityId === upper(itemField(ability, 'item_id'));
  const debit = (cost: number): void => {
    const next = requiredInt(ecLean(caster, 'aps'), 'template_spell_roll:aps-not-held') - cost;
    casterEc.aps = finish ? Math.max(0, Math.min(127, next)) : next;
  };
  // A COUNTERED caster loses exactly 1 AP (template_spell_roll.php ~L559/591 — only a 2+ AP cast is counterable) and the per-turn
  // attack counter does not tick (the attack never happened).
  if (countered) { debit(1); return; }
  // R-128: an engine-fired cast (a contingency) spends no AP and ticks no attack counter; a reaction casting time still spends the reaction
  if (engineFired(ctx, ability, caster)) {
    if (castingTime === 'reaction') { requiredInt(ecLean(caster, 'reaction'), 'template_spell_roll:reaction-not-held'); casterEc.reaction = 0; }
    return;
  }
  if (castingTime === 'reaction') { requiredInt(ecLean(caster, 'reaction'), 'template_spell_roll:reaction-not-held'); casterEc.reaction = 0; }
  else {
    const match = /^(\d+)ap$/.exec(['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'].includes(castingTime) ? '3ap' : castingTime);
    if (!match) handBack('template_spell_roll:casting-time-action');
    // Wizard v9 next_summon_ap_override (summon.ts): a summon spell's AP capped by the armed status, which it consumes
    const cost = summonApCost(ctx, ability, caster, Math.min(requiredInt(match[1], 'template_spell_roll:invalid-action-cost'), nextSpellPlan(ability)?.ap ?? Infinity));
    debit(cost);
  }
  if (['5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C']
      .includes(String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase()))
    casterEc.weapon_attack = requiredInt(ecLean(caster, 'weapon_attack'), 'template_spell_roll:weapon-attack-not-held') + 1;
}

/** `skipDispatch`: an instant area cast on empty ground (owner ruling 27, janus.ts) — every gate and cost of the template, no roll dispatch. */
export function templateSpellRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[], opts?: { skipDispatch?: boolean }): void {
  spellManaPreDraw(ctx, input, ability, caster);
  const usesItemType = planCasterUsesItemType(ctx, ability, caster);
  // armor_bending_roll() (lane AB1, R-125 — armor-bending.ts): a FAILED roll spends the action (the post-roll resource hook, AP, an innate
  // charge) with no mana and no effect — no counterspell check, no dispatch; a tie's surge is logged and the spell goes on.
  // hostile_spell_miscast_roll() (layer hostile_spell_miscast_chance_in_encounter — hostile-spell-miscast.ts): after a passed / absent
  // armor bending roll, an opposing holder's d100 may MISCAST the spell — the same failure as a failed armor bending roll. The holder is
  // proven BEFORE the armor bending d20 (its hand-backs stay pre-draw), then re-read after it like PHP (a tie's surge may change the roster).
  hostileSpellMiscastHolder(ctx, ability, caster);
  const bending = armorBendingRoll(ctx, input, ability, caster);
  if (bending === 'failed' || hostileSpellMiscastRoll(ctx, hostileSpellMiscastHolder(ctx, ability, caster), caster, upper(input.abilityId))) {
    settleSpellResources(ctx, input, ability, caster, { skipMana: true });
    spendSpellAction(ctx, ability, caster);
    return;
  }
  fireCasterUsesItemType(ctx, usesItemType);
  // counterspell_check: an armed watch may interrupt BEFORE any resource is consumed — a countered caster keeps its mana and charges
  // and skips the roll dispatch entirely.
  if (counterspellCheck(ctx, input, ability, caster)) { spendSpellAction(ctx, ability, caster, true); return; }
  consumeSpellMaterials(ctx, ability, caster);
  captureAreaVfx(ctx, input, ability, caster);
  applyIceWallDisplacements(ctx, input);
  if (!opts?.skipDispatch) dispatchNextSpell(ctx, input, ability, caster, targets);
  settleSpellResources(ctx, input, ability, caster);
  spendSpellAction(ctx, ability, caster);
}

type SpellDiscountPlan = { discount: number; carriers: ReturnType<typeof nextSpellManaDiscountCarriers> };
const SPELL_DISCOUNT_PLANS = new WeakMap<JanusCtx, Map<string, SpellDiscountPlan>>();

/** Freeze the price and exact one-use rows before dispatch, so newly granted statuses cannot change this cast's bill. */
function spellDiscountPlan(ctx: JanusCtx, ability: Row, caster: Row): SpellDiscountPlan {
  if (ctx.trigger) return { discount: 0, carriers: [] };
  const key = `${ecId(caster)}|${upper(itemField(ability, 'item_id'))}`;
  let plans = SPELL_DISCOUNT_PLANS.get(boundarySource(ctx));
  const held = plans?.get(key);
  if (held) return held;
  const read = { ...ctxProviderReaders(ctx), statuses: (row: Row) => activeStatuses(ctx, row) };
  const carriers = nextSpellManaDiscountCarriers(read, caster, ability);
  for (const { status } of carriers) planStoredStatusRemoval(ctx, caster, status, 'single');
  const plan = { discount: carriers.reduce((n, c) => n + c.discount, 0), carriers };
  if (!plans) SPELL_DISCOUNT_PLANS.set(boundarySource(ctx), plans = new Map());
  plans.set(key, plan);
  return plan;
}

/** A failed/countered branch may debit zero; preview the payable branch before armor/counterspell dice. */
export function spellManaPreDraw(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  preflightNextSpell(ctx, ability, caster);
  const level = ctx.read.has(ability, 'spell_level') ? requiredInt(ctx.read.value(ability, 'spell_level'), 'template_spell_roll:invalid-level') : 0;
  const freeCast = spellCastIsFree(ctx, input);
  if (level <= 0 || freeCast || engineFired(ctx, ability, caster)) return;
  const { discount } = spellDiscountPlan(ctx, ability, caster);
  // Wizard v9 spell_mana_discount: a discount that zeroes the cost spends no mana (no mana-spend trigger to prove)
  if (discountedSpellMana(Math.max(level, ctx.castLevel ?? level), discount + ctxSpellManaDiscount(ctx, caster, ability)) > 0)
    manaSpendPreDraw(ctx, caster, itemField(ability, 'item_type') === 'spell' ? level : null);
}

/** janus_grant_spellblade_aether_on_mana_spell_cast()'s reach: the caster holds the ACTIVE Aether passive and the spell does not spend Aether
 *  itself (its EXCLUSION — a spell carrying consumes_ability_charges = Aether never also generates it: Blinkwire, Aether Combo …). */
function aetherGrantApplies(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  if (ctx.read.entries(ability, 'consumes_ability_charges').some((e) => upper(typeof e === 'string' ? e : record(e)?.item_id ?? record(e)?.value
    ?? itemField(record(e), 'item_id')) === AETHER_MECHANIC)) return false;
  // the grant's loop skips a non-array entry, then passive_ability_entry_is_active(): no stamp = active, else PHP truthiness
  // (`!empty()` — 0 / '0' / '' / null / false are inactive; Codex review 2026-10-09)
  return ctx.read.entries(caster, 'passive_ability').some((entry) => {
    const row = record(entry);
    if (!row) return false;
    const id = upper(row.ability_id ?? itemField(record(row.item_arr), 'item_id') ?? row.item_arr ?? itemField(row, 'item_id'));
    if (id !== AETHER_MECHANIC) return false;
    if (!('passive_conditions_active' in row)) return true;
    const stamp = row.passive_conditions_active;
    return !(stamp == null || stamp === false || stamp === 0 || stamp === '' || stamp === '0' || (Array.isArray(stamp) && !stamp.length));
  });
}

/** template_spell_roll's CHARGE SETTLEMENT, proven before any die (Wizard v9, 2026-10-09 — was the blanket
 *  `template_spell_roll:charge-settlement-not-modeled` hand-back): the post-roll hook's own ability_charges pool and consumes_ability_charges
 *  (consumes_charges_qty, [x] / [max] / [any], waivers, the Mastery marker) — settleSpellResources spends them after the dispatch exactly as
 *  janus_post_roll_change_ability_resources() does; and the Aether pool a mana-paying cast will grant into. */
export function spellChargeSettlementPreDraw(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  preflightInnateSpellUse(ctx, input, ability, caster);
  preflightPostRollChargeResources(ctx, ability, caster, input.useAbilityVariable1);
  const level = ctx.read.has(ability, 'spell_level') ? requiredInt(ctx.read.value(ability, 'spell_level'), 'template_spell_roll:invalid-level') : 0;
  if (level > 0 && manaReductionDelta(caster, -level, resourceReductionReaders(ctx)) < 0 && !spellCastIsFree(ctx, input) && !engineFired(ctx, ability, caster)
      && aetherGrantApplies(ctx, ability, caster)) abilityChargeEntry(ctx, caster, AETHER_MECHANIC);
}

/** template_spell_roll.php after the dispatch, for an uncountered cast: the mana debit (+ the Aether grant it triggers) and the post-roll
 *  resource hook. Shared by every spell path — the no-roll persistent-area shortcut in janus.ts too (Codex review 2026-09-29). */
export function settleSpellResources(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, opts?: { skipMana?: boolean }): void {
  const level = ctx.read.has(ability, 'spell_level') ? requiredInt(ctx.read.value(ability, 'spell_level'), 'template_spell_roll:invalid-level') : 0;
  // "THE INT PASSED IS MANA USED": a level-cast spell pays its cast level (upcast.ts, Session 1 step 5a)
  const finish = ctx.timedFinish?.casterId === upper(input.casterId) && ctx.timedFinish?.abilityId === upper(input.abilityId);
  const freeCast = spellCastIsFree(ctx, input);
  // `skipMana`: a failed armor bending roll (template_spell_roll.php's mana block is gated on !$failed_armor_bending_roll)
  // R-128: a contingency's mana was PRE-PAID by its arming; neither it nor a free cast uses a one-use discount.
  if (level > 0 && !freeCast && !opts?.skipMana && !engineFired(ctx, ability, caster)) {
    const discountPlan = spellDiscountPlan(ctx, ability, caster);
    const before = requiredInt(ecLean(caster, 'mana'), 'template_spell_roll:mana-not-held');
    // Wizard v9 spell_mana_discount (caster-spell-layers.ts): the cast level's cost less the summed in-scope discount, floor 0
    const paidLevel = Number(record(ability.computed)?.sanguine_paid_level ?? ctx.castLevel ?? level);
    const after = setMana(ctx, caster, before - discountedSpellMana(Math.max(level, paidLevel), discountPlan.discount + ctxSpellManaDiscount(ctx, caster, ability)));
    if (after < before) manaSpendPreDraw(ctx, caster, itemField(ability, 'item_type') === 'spell' ? level : null);
    // Aether gains the LEVEL CAST, including upcasts, independently of any mana discount.
    if (after < before && aetherGrantApplies(ctx, ability, caster))
      adjustCharges(ctx, caster, AETHER_MECHANIC, Math.max(level, paidLevel), true);
    fireManaSpend(ctx, caster, before, after, itemField(ability, 'item_type') === 'spell' ? level : null);
    for (const { status } of discountPlan.carriers) {
      if (ctx.statusesRemoved.some(removed => upper(removed.status_id) === upper(status.status_id))) continue;
      removeStoredStatus(ctx, caster, status, 'single');
      refundMysteriousCarrier(ctx, caster, status, level);
    }
  }
  // consume resources (template_spell_roll.php ~L435, skipped when countered): the same post-roll hook the ability template runs.
  janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, undefined, input);
  settleInnateSpellUse(ctx, input, ability, caster);
  if (!opts?.skipMana) settleNextSpell(ctx, ability, caster);
  // All committed spell paths settle here, including persistent areas; an interrupted cast never reaches settlement.
  if (itemField(ability, 'item_type') === 'spell') {
    const consumed = new Set<string>();
    for (const status of mysteriousCarriers(ctx, caster)) {
      const id = upper(status.status_id);
      if (consumed.has(id)) continue;
      consumed.add(id);
      removeStoredStatus(ctx, caster, status, 'single');
      refundMysteriousCarrier(ctx, caster, status, level);
    }
  }
  // ft_charged_cast_marker + consume_inventory_charges: the timed START never bills the item, its finish does.
  if (finish && input.useAbilityVariable1 === 'finish_casting' && ['charged_spell', 'charged_ability'].includes(String(input.useAbilityVariable2))) {
    const source = ctx.timedFinish!.variable2Arr;
    const id = upper(ctx.read.inventoryField?.(source, 'inventory_id'));
    const raw = source ? ctx.read.value(source, 'current_item_charges') : null;
    if (!source || !id || raw == null || raw === '' || !Array.isArray(caster.inventory) || !caster.inventory.length) return;
    const cost = input.useAbilityVariable2 === 'charged_spell' ? level : int(ctx.read.value(ability, 'ability_activation_charges'));
    settleSourceCharges(ctx, caster, id, source, sourceItemType(ctx.read, source) ?? '', cost, raw);
  }
}
